import React, {useRef, useEffect, memo, Suspense} from "react"
import {MANIFEST} from "@manifest/header"
import {TemplateSettingsModel, useTemplatedComponent} from "@next/templating"
import connect from "./connect"
import {ShoppingBag} from "../../models/shoppingbag"
import MiniShoppingBagItem from "../MiniShoppingBagItem"
import ChargesAndIncentiveList from "../ChargesAndIncentiveList"
import {bagItemsWheelLock} from "../../utils/wheelLock"
import {PromotionWrapper, ItemsContainer, MiniShoppingBagContainer, PromotionDiscount, PromotionText} from "./component"

export type MiniShoppingBagItemsProps = {
    text: any
    bag: ShoppingBag
} & TemplateSettingsModel

export const MiniShoppingBagItems = memo(({mainTemplate, fallbackTemplate, text, bag}: MiniShoppingBagItemsProps) => {
    const {
        Items: items,
        Description,
        MultiBuyDiscount,
        FinalOrderValueFormatted,
        MultiBuyDiscountFormatted,
        ChargesAndIncentives,
    } = bag
    const bagItemsContainerRef = useRef<HTMLDivElement | null>(null)
    const bagItemsRef = useRef<HTMLDivElement | null>(null)

    const handleWheelLock = e => bagItemsWheelLock(e, bagItemsContainerRef, bagItemsRef)

    useEffect(() => {
        const bagItemsContainer = bagItemsContainerRef.current
        // eslint-disable-next-line no-unused-expressions
        bagItemsContainer?.addEventListener("wheel", handleWheelLock, {passive: false})
        return () => {
            // eslint-disable-next-line no-unused-expressions
            bagItemsContainer?.removeEventListener("wheel", handleWheelLock)
        }
    }, [])

    const ComponentName = useTemplatedComponent({mainTemplate, fallbackTemplate}, MANIFEST)
    return (
        <MiniShoppingBagContainer ref={bagItemsContainerRef}>
            <ItemsContainer data-testid="header-mini-shopping-bag-items" ref={bagItemsRef}>
                {items.map(item => (
                    <MiniShoppingBagItem item={item} key={item.ItemID} text={text} />
                ))}
            </ItemsContainer>
            {MultiBuyDiscount > 0 && (
                <PromotionWrapper>
                    <PromotionText>{text.promotionalText}</PromotionText>
                    <PromotionDiscount data-testid="header-mini-shopping-bag-multibuy-discount">
                        {`${text.negative}${MultiBuyDiscountFormatted}`}
                    </PromotionDiscount>
                </PromotionWrapper>
            )}
            {ChargesAndIncentives && !!ChargesAndIncentives.length && (
                <ChargesAndIncentiveList incentives={ChargesAndIncentives} />
            )}
            <Suspense>
                <ComponentName
                    isFirstSection={MultiBuyDiscount === 0 && ChargesAndIncentives && !ChargesAndIncentives.length}
                    totalText={text.totalText}
                    finalOrderValueFormatted={FinalOrderValueFormatted}
                    description={Description}
                />
            </Suspense>
        </MiniShoppingBagContainer>
    )
})
MiniShoppingBagItems.displayName = "MiniShoppingBagItems"

export default connect(MiniShoppingBagItems)
